/**
 * Catalog / PIM fixtures matching the ratified CODEX-3 contract
 * (internal/catalog/openapi-draft/catalog.openapi.yaml): products + variants
 * + attributes (with per-product assignments) + UoM, barcodes with CORRECT
 * check digits (computed, then independently re-checked in tests against the
 * scanner package's validators), one bundle with BOM components, and one
 * attempted-cycle example the handlers reject with `bom_cycle_detected`.
 */
import type { components } from "@erp/api-client";

import { ean13, gtin14, upcA } from "../helpers/gtin";
import * as id from "./ids";
import { fixtureId } from "./ids";

type Schemas = components["schemas"];

export type CatalogLifecycleStatus = Schemas["CatalogLifecycleStatus"];

// ── UoM & conversions ────────────────────────────────────────────────────
export type Uom = Schemas["CatalogUom"];

export const uoms: Uom[] = [
  {
    id: id.UOM_EA,
    code: "EA",
    name: "Each",
    symbol: "ea",
    dimension: "count",
    status: "active",
    created_at: "2026-05-01T08:00:00Z",
    updated_at: "2026-05-01T08:00:00Z",
  },
  {
    id: id.UOM_BOX,
    code: "BOX",
    name: "Box of 12",
    symbol: "box",
    dimension: "count",
    status: "active",
    created_at: "2026-05-01T08:01:00Z",
    updated_at: "2026-05-01T08:01:00Z",
  },
  // KG has deliberately NO conversion path to EA (different dimension) — the
  // `uom_unconvertible` typed-error demos key off it.
  {
    id: id.UOM_KG,
    code: "KG",
    name: "Kilogram",
    symbol: "kg",
    dimension: "mass",
    status: "active",
    created_at: "2026-05-01T08:02:00Z",
    updated_at: "2026-05-01T08:02:00Z",
  },
];

/** Composite key {from_uom_id, to_uom_id} — the ratified shape has NO `id`. */
export type UomConversion = Schemas["CatalogUomConversion"];

export const uomConversions: UomConversion[] = [
  {
    from_uom_id: id.UOM_BOX,
    to_uom_id: id.UOM_EA,
    factor: "12.0000000000",
    created_at: "2026-05-01T09:00:00Z",
    updated_at: "2026-05-01T09:00:00Z",
  },
];

// ── Attributes ───────────────────────────────────────────────────────────
export type Attribute = Schemas["CatalogAttribute"];

export const attributes: Attribute[] = [
  {
    id: id.ATTR_COLOR,
    code: "color",
    name: "Color",
    type: "select",
    options: ["red", "blue", "black"],
    status: "active",
    created_at: "2026-05-01T10:00:00Z",
    updated_at: "2026-05-01T10:00:00Z",
  },
  {
    id: id.ATTR_SIZE,
    code: "size",
    name: "Size",
    type: "select",
    options: ["S", "M", "L", "XL"],
    status: "active",
    created_at: "2026-05-01T10:01:00Z",
    updated_at: "2026-05-01T10:01:00Z",
  },
  // Deliberately assigned to NO product — the product editor's assign-picker
  // demos/tests key off it (assign → the variant grid gains the column).
  {
    id: fixtureId("a002", 3),
    code: "material",
    name: "Material",
    type: "text",
    options: [],
    status: "active",
    created_at: "2026-05-01T10:02:00Z",
    updated_at: "2026-05-01T10:02:00Z",
  },
];

// ── Tax classes & categories ─────────────────────────────────────────────
/** CatalogTaxClassLookup — the list projection is closed {id, name}. */
export type TaxClass = Schemas["CatalogTaxClassLookup"];

export const taxClasses: TaxClass[] = [{ id: id.TAX_STD, name: "Standard rate 15%" }];

export type CategoryPathSegment = Schemas["CatalogCategoryPathSegment"];
export type Category = Schemas["CatalogCategory"];

const rootCategory = (categoryId: string, code: string, name: string): Category => ({
  id: categoryId,
  code,
  name,
  description: "",
  status: "active",
  parent_id: null,
  depth: 0,
  path: [{ id: categoryId, code, name }],
  has_children: false,
  created_at: "2026-05-01T11:00:00Z",
  updated_at: "2026-05-01T11:00:00Z",
});

export const categories: Category[] = [
  rootCategory(id.CAT_APPAREL, "APPAREL", "Apparel"),
  rootCategory(id.CAT_ELECTRONICS, "ELECTRONICS", "Electronics"),
  rootCategory(id.CAT_BIKES, "BIKES", "Bikes"),
];

// ── Products & variants ──────────────────────────────────────────────────
export type Product = Omit<
  Schemas["CatalogProduct"],
  "matched_variant" | "variant_count" | "variants"
> &
  Required<Pick<Schemas["CatalogProduct"], "category_id" | "tax_class_id">>;

export const products: Product[] = [
  {
    id: id.PROD_TSHIRT,
    name: "Cotton T-Shirt",
    description: "Crew-neck cotton t-shirt, 180gsm.",
    product_type: "goods",
    tax_class_id: id.TAX_STD,
    base_uom_id: id.UOM_EA,
    category_id: id.CAT_APPAREL,
    status: "active",
    created_at: "2026-05-02T08:00:00Z",
    updated_at: "2026-08-01T10:15:00Z",
  },
  {
    id: id.PROD_CABLE,
    name: "USB-C Cable 2m",
    description: "Braided USB-C to USB-C cable, 2 metres, 100W.",
    product_type: "goods",
    tax_class_id: id.TAX_STD,
    base_uom_id: id.UOM_EA,
    category_id: id.CAT_ELECTRONICS,
    status: "active",
    created_at: "2026-05-02T08:05:00Z",
    updated_at: "2026-08-05T09:00:00Z",
  },
  {
    id: id.PROD_WHEEL_SET,
    name: "Bike Wheel Set",
    description: "Pair of 700c alloy wheels.",
    product_type: "goods",
    tax_class_id: id.TAX_STD,
    base_uom_id: id.UOM_EA,
    category_id: id.CAT_BIKES,
    status: "active",
    created_at: "2026-05-03T08:00:00Z",
    updated_at: "2026-07-20T12:00:00Z",
  },
  {
    id: id.PROD_FRAME_KIT,
    name: "Bike Frame Kit (Bundle)",
    description: "Frame kit bundle: wheel set plus routing cable.",
    product_type: "bundle",
    tax_class_id: id.TAX_STD,
    base_uom_id: id.UOM_EA,
    category_id: id.CAT_BIKES,
    status: "active",
    created_at: "2026-05-04T08:00:00Z",
    updated_at: "2026-07-22T12:00:00Z",
  },
];

export type Variant = Schemas["CatalogVariant"];

export const variants: Variant[] = [
  {
    id: id.VAR_TSHIRT_RED_L,
    product_id: id.PROD_TSHIRT,
    sku: "TSHIRT-RED-L",
    attribute_values: { color: "red", size: "L" },
    status: "active",
    created_at: "2026-05-02T08:10:00Z",
    updated_at: "2026-05-02T08:10:00Z",
  },
  {
    id: id.VAR_TSHIRT_BLU_M,
    product_id: id.PROD_TSHIRT,
    sku: "TSHIRT-BLU-M",
    attribute_values: { color: "blue", size: "M" },
    status: "active",
    created_at: "2026-05-02T08:11:00Z",
    updated_at: "2026-05-02T08:11:00Z",
  },
  {
    id: id.VAR_TSHIRT_BLK_S,
    product_id: id.PROD_TSHIRT,
    sku: "TSHIRT-BLK-S",
    attribute_values: { color: "black", size: "S" },
    status: "inactive",
    created_at: "2026-05-02T08:12:00Z",
    updated_at: "2026-07-15T12:00:00Z",
  },
  {
    id: id.VAR_CABLE,
    product_id: id.PROD_CABLE,
    sku: "WID-CBL-2M",
    attribute_values: {},
    status: "active",
    created_at: "2026-05-02T08:20:00Z",
    updated_at: "2026-05-02T08:20:00Z",
  },
  {
    id: id.VAR_WHEEL_SET,
    product_id: id.PROD_WHEEL_SET,
    sku: "WHEEL-SET",
    attribute_values: {},
    status: "active",
    created_at: "2026-05-03T08:10:00Z",
    updated_at: "2026-05-03T08:10:00Z",
  },
  {
    id: id.VAR_FRAME_KIT,
    product_id: id.PROD_FRAME_KIT,
    sku: "FRAME-KIT",
    attribute_values: {},
    status: "active",
    created_at: "2026-05-04T08:10:00Z",
    updated_at: "2026-05-04T08:10:00Z",
  },
];

// ── Per-product attribute assignments ────────────────────────────────────
// Every attribute a fixture variant carries a value for MUST be assigned to
// its product — the server rejects values for unassigned codes.
export type ProductAttributeAssignment = Schemas["CatalogProductAttribute"];

export const productAttributes: ProductAttributeAssignment[] = [
  {
    product_id: id.PROD_TSHIRT,
    attribute_id: id.ATTR_COLOR,
    is_required: true,
    is_variant_axis: true,
    position: 0,
    created_at: "2026-05-02T08:01:00Z",
    updated_at: "2026-05-02T08:01:00Z",
  },
  {
    product_id: id.PROD_TSHIRT,
    attribute_id: id.ATTR_SIZE,
    is_required: true,
    is_variant_axis: true,
    position: 1,
    created_at: "2026-05-02T08:02:00Z",
    updated_at: "2026-05-02T08:02:00Z",
  },
];

// ── Barcodes ─────────────────────────────────────────────────────────────
// Check digits are COMPUTED here (helpers/gtin) and independently re-checked
// by the fixture-integrity tests against features/shared/scanner validators.
export type VariantBarcode = Schemas["CatalogBarcode"];

export const BARCODE_TSHIRT_RED_L = ean13("628112345001");
export const BARCODE_TSHIRT_BLU_M = ean13("628112345002");
export const BARCODE_CABLE = upcA("04210000512");
export const BARCODE_WHEEL_SET = gtin14("1628112345009");
export const BARCODE_FRAME_KIT = "FRAME-KIT-0001";

const barcodeRow = (
  n: number,
  variantId: string,
  barcode: string,
  type: VariantBarcode["type"],
): VariantBarcode => ({
  id: fixtureId("a013", n),
  variant_id: variantId,
  barcode,
  type,
  created_at: "2026-05-05T08:00:00Z",
  updated_at: "2026-05-05T08:00:00Z",
});

export const variantBarcodes: VariantBarcode[] = [
  barcodeRow(1, id.VAR_TSHIRT_RED_L, BARCODE_TSHIRT_RED_L, "EAN13"),
  barcodeRow(2, id.VAR_TSHIRT_BLU_M, BARCODE_TSHIRT_BLU_M, "EAN13"),
  barcodeRow(3, id.VAR_CABLE, BARCODE_CABLE, "UPC"),
  barcodeRow(4, id.VAR_WHEEL_SET, BARCODE_WHEEL_SET, "GTIN14"),
  barcodeRow(5, id.VAR_FRAME_KIT, BARCODE_FRAME_KIT, "CODE128"),
];

// ── Bundle / BOM components ──────────────────────────────────────────────
export type ProductComponent = Schemas["CatalogProductComponent"];

export const productComponents: ProductComponent[] = [
  {
    id: id.COMP_FRAME_WHEELS,
    parent_product_id: id.PROD_FRAME_KIT,
    component_variant_id: id.VAR_WHEEL_SET,
    qty: "2.000000",
    uom_id: id.UOM_EA,
    component_type: "bundle",
    position: 0,
    created_at: "2026-05-04T09:00:00Z",
    updated_at: "2026-05-04T09:00:00Z",
  },
  {
    id: id.COMP_FRAME_CABLE,
    parent_product_id: id.PROD_FRAME_KIT,
    component_variant_id: id.VAR_CABLE,
    qty: "1.000000",
    uom_id: id.UOM_EA,
    component_type: "bundle",
    position: 1,
    created_at: "2026-05-04T09:01:00Z",
    updated_at: "2026-05-04T09:01:00Z",
  },
];

// ── Product imports (two-phase CSV wizard) ───────────────────────────────
// One staged-and-validated import with a mixed clean/error row report. The
// import handlers copy this seed into mutable state (commit/discard mutate a
// copy, never these records).
export type ProductImport = Schemas["CatalogProductImport"];
export type ProductImportErrorField = Schemas["CatalogProductImportErrorField"];
export type ProductImportRowError = Schemas["CatalogProductImportRowError"];
export type ProductImportNormalizedRow = Schemas["CatalogProductImportNormalizedRow"];
export type ProductImportRow = Schemas["CatalogProductImportRow"];

/** The server-assigned opaque filename token for an import id. */
export const productImportFilename = (importId: string): string => `catalog-import-${importId}.csv`;

export const makeProductImportRow = (
  idSeed: number,
  importId: string,
  rowNo: number,
  sku: string,
  status: ProductImportRow["status"],
  errors: ProductImportRowError[],
  normalized?: Partial<ProductImportNormalizedRow>,
): ProductImportRow => ({
  id: fixtureId("a101", idSeed),
  import_id: importId,
  row_no: rowNo,
  sku,
  status,
  errors,
  normalized: { action: "upsert", sku, product_name: `Imported ${sku}`, ...normalized },
});

export const productImportRows: ProductImportRow[] = [
  makeProductImportRow(1, id.IMPORT_VALIDATED, 1, "IMP-TSHIRT-GRN-M", "valid", []),
  makeProductImportRow(2, id.IMPORT_VALIDATED, 2, "IMP-TSHIRT-GRN-L", "valid", []),
  makeProductImportRow(3, id.IMPORT_VALIDATED, 3, "IMP-CABLE-3M", "valid", []),
  // Deliberately duplicates an existing catalog SKU → the per-row error chip.
  makeProductImportRow(4, id.IMPORT_VALIDATED, 4, "TSHIRT-RED-L", "error", [
    { code: "sku_duplicate", field: "sku", message: "SKU already exists in the catalog." },
  ]),
];

export const productImports: ProductImport[] = [
  {
    id: id.IMPORT_VALIDATED,
    filename: productImportFilename(id.IMPORT_VALIDATED),
    status: "validated",
    total_rows: productImportRows.length,
    clean_count: productImportRows.filter((row) => row.status === "valid").length,
    error_count: productImportRows.filter((row) => row.status === "error").length,
    committed_count: 0,
    error_csv_available: productImportRows.some((row) => row.status === "error"),
    staging_expires_at: "2026-08-16T10:00:00Z",
    created_at: "2026-08-09T10:00:00Z",
    updated_at: "2026-08-09T10:05:00Z",
  },
];

/**
 * The attempted-cycle example: adding FRAME-KIT as a component of WHEEL-SET
 * would close the loop FRAME-KIT → WHEEL-SET → FRAME-KIT. The catalog handler
 * rejects exactly this insert with 422 `bom_cycle_detected` carrying the
 * `cycle` extension (loop path as SKUs) per the catalog SPEC screen 6.
 */
export const attemptedCycleComponent = {
  parent_product_id: id.PROD_WHEEL_SET,
  component_variant_id: id.VAR_FRAME_KIT,
  qty: "1.000000",
  uom_id: id.UOM_EA,
  component_type: "bundle" as const,
  rejected_with: "bom_cycle_detected",
  cycle: ["FRAME-KIT", "WHEEL-SET", "FRAME-KIT"],
};
